/**
 * The one place authenticated calls go through.
 *
 * Its job beyond `fetch` is small but load-bearing: a 401 anywhere in the app
 * tells the auth store that the device is out, so a revoked iPad locks itself on
 * its very next request instead of at the next cold start.
 *
 * Note what it deliberately does not do: it does not retry, and it does not
 * touch the sync queue. `sync.ts` treats a 401 as "not now" and keeps the game,
 * because that queue holds the only copy of an evening's scores.
 */

import { useAuth } from '@/store/auth';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the call failed because the network did, not the server. */
  get offline(): boolean {
    return this.status === 0;
  }
}

async function messageOf(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    if (typeof body.error === 'string' && body.error) return body.error;
  } catch {
    // Not JSON — an nginx page, or an empty body.
  }
  return `Er ging iets mis (${response.status}).`;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch {
    // Distinguished from every server answer by status 0, so callers can show
    // "geen verbinding" rather than blaming the user.
    throw new ApiError(0, 'Geen verbinding met de server.');
  }

  if (response.status === 401) {
    useAuth.getState().onUnauthorized();
    throw new ApiError(401, await messageOf(response));
  }

  if (!response.ok) throw new ApiError(response.status, await messageOf(response));

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
